import * as React from 'react';
import { Spinner, Typography } from '@/components/common';
import {
	DATA_TYPE_LABELS,
	DetectedPdfField,
	FieldConfig,
	PdfPageViewport,
} from '@/interfaces';
import {
	detectPdfFields,
	isConfigurablePdfField,
} from './lib/pdfFieldDetection';
import { loadPdfJs } from './lib/loadPdfJs';

const PAPER_CONTENT_WIDTH = 660 - 48 * 2;

export interface PdfFieldCanvasProps {
	pdfUrl: string;
	onFieldsDetected?: (fields: DetectedPdfField[]) => void;
	onViewportsReady?: (viewports: PdfPageViewport[]) => void;
	fieldConfigs: Record<string, FieldConfig>;
	selectedFieldId: string | null;
	onFieldClick?: (fieldId: string) => void;
	zoomScale?: number;
	onNumPagesChange?: (numPages: number) => void;
	onCurrentPageChange?: (pageIndex: number) => void;
	baseContentWidth?: number;
	onLoadError?: (message: string) => void;
}

export interface PdfFieldCanvasHandle {
	scrollToPage: (pageIndex: number) => void;
}
const findScrollParent = (node: HTMLElement | null): HTMLElement | null => {
	let el = node?.parentElement ?? null;
	while (el) {
		const { overflowY } = window.getComputedStyle(el);
		if (overflowY === 'auto' || overflowY === 'scroll') return el;
		el = el.parentElement;
	}
	return null;
};

export const PdfFieldCanvas = React.forwardRef<
	PdfFieldCanvasHandle,
	PdfFieldCanvasProps
>(function PdfFieldCanvas(
	{
		pdfUrl,
		onFieldsDetected,
		onViewportsReady,
		fieldConfigs,
		selectedFieldId,
		onFieldClick,
		zoomScale = 1,
		onNumPagesChange,
		onCurrentPageChange,
		baseContentWidth = PAPER_CONTENT_WIDTH,
		onLoadError,
	},
	ref
) {
	const [isLoading, setIsLoading] = React.useState(true);
	const [error, setError] = React.useState<string | null>(null);
	const [pdfDocument, setPdfDocument] = React.useState<any>(null);
	const [numPages, setNumPages] = React.useState(0);
	const [pageViewports, setPageViewports] = React.useState<PdfPageViewport[]>(
		[]
	);
	const [detectedFields, setDetectedFields] = React.useState<
		DetectedPdfField[]
	>([]);

	const [pagesContainerNode, setPagesContainerNode] =
		React.useState<HTMLDivElement | null>(null);

	const canvasRefs = React.useRef<Map<number, HTMLCanvasElement>>(new Map());
	const pageViewportInstances = React.useRef<Map<number, any>>(new Map());
	const overlayRefs = React.useRef<Map<string, HTMLButtonElement>>(new Map());
	const pageContainerRefs = React.useRef<Map<number, HTMLDivElement>>(
		new Map()
	);

	React.useImperativeHandle(
		ref,
		() => ({
			scrollToPage: (pageIndex: number) => {
				pageContainerRefs.current
					.get(pageIndex)
					?.scrollIntoView({ behavior: 'smooth', block: 'start' });
			},
		}),
		[]
	);

	const [renderZoomScale, setRenderZoomScale] = React.useState(zoomScale);
	const lastRenderedZoomRef = React.useRef<number | null>(null);
	React.useEffect(() => {
		const timeoutId = setTimeout(() => setRenderZoomScale(zoomScale), 120);
		return () => clearTimeout(timeoutId);
	}, [zoomScale]);

	React.useEffect(() => {
		if (!pdfUrl) return undefined;

		let isCancelled = false;

		const loadPdf = async (): Promise<void> => {
			try {
				setIsLoading(true);
				setError(null);

				const pdfjsLib = await loadPdfJs();
				const loadingTask = pdfjsLib.getDocument(pdfUrl);
				const pdf = await loadingTask.promise;

				if (isCancelled) return;

				setPdfDocument(pdf);
				setNumPages(pdf.numPages);
				onNumPagesChange?.(pdf.numPages);
			} catch (err) {
				console.error('Error loading PDF:', err);
				if (!isCancelled) {
					setError('Error loading the PDF document');
					onLoadError?.('There was an error reading this PDF file.');
				}
			} finally {
				if (!isCancelled) setIsLoading(false);
			}
		};

		loadPdf();

		return () => {
			isCancelled = true;
		};
	}, [pdfUrl]);

	React.useEffect(() => {
		if (!pdfDocument) return undefined;

		let isCancelled = false;

		detectPdfFields(pdfDocument)
			.then((fields) => {
				if (isCancelled) return;
				setDetectedFields(fields);
				onFieldsDetected?.(fields);
			})
			.catch((err) => {
				console.error('Error detecting PDF fields:', err);
			});

		return () => {
			isCancelled = true;
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [pdfDocument]);

	React.useEffect(() => {
		if (!pdfDocument || !pagesContainerNode) return undefined;

		let isCancelled = false;

		const scrollRoot = findScrollParent(pagesContainerNode);
		const isZoomChange =
			lastRenderedZoomRef.current !== null &&
			lastRenderedZoomRef.current !== renderZoomScale;
		const scrollAnchor =
			isZoomChange && scrollRoot && scrollRoot.scrollHeight > 0
				? {
						y:
							(scrollRoot.scrollTop + scrollRoot.clientHeight / 2) /
							scrollRoot.scrollHeight,
						x:
							(scrollRoot.scrollLeft + scrollRoot.clientWidth / 2) /
							scrollRoot.scrollWidth,
				  }
				: null;
		lastRenderedZoomRef.current = renderZoomScale;

		const renderPages = async (): Promise<void> => {
			const containerWidth = baseContentWidth;
			const viewports: PdfPageViewport[] = [];

			for (
				let pageIndex = 0;
				pageIndex < pdfDocument.numPages;
				pageIndex += 1
			) {
				// eslint-disable-next-line no-await-in-loop
				const page = await pdfDocument.getPage(pageIndex + 1);
				if (isCancelled) return;

				const baseViewport = page.getViewport({ scale: 1 });
				const fitScale = containerWidth / baseViewport.width;
				const viewport = page.getViewport({
					scale: fitScale * renderZoomScale,
				});

				const canvas = canvasRefs.current.get(pageIndex);
				if (!canvas) continue;

				const context = canvas.getContext('2d');
				const outputScale = window.devicePixelRatio || 1;

				canvas.width = Math.floor(viewport.width * outputScale);
				canvas.height = Math.floor(viewport.height * outputScale);
				canvas.style.width = `${Math.floor(viewport.width)}px`;
				canvas.style.height = `${Math.floor(viewport.height)}px`;

				if (outputScale !== 1 && context) {
					context.setTransform(outputScale, 0, 0, outputScale, 0, 0);
				}

				// eslint-disable-next-line no-await-in-loop
				await page.render({ canvasContext: context, viewport }).promise;

				pageViewportInstances.current.set(pageIndex, viewport);

				viewports.push({
					pageIndex,
					width: viewport.width,
					height: viewport.height,
				});
			}

			if (!isCancelled) {
				setPageViewports(viewports);
				onViewportsReady?.(viewports);
				if (scrollAnchor && scrollRoot) {
					requestAnimationFrame(() => {
						scrollRoot.scrollTop =
							scrollAnchor.y * scrollRoot.scrollHeight -
							scrollRoot.clientHeight / 2;
						scrollRoot.scrollLeft =
							scrollAnchor.x * scrollRoot.scrollWidth -
							scrollRoot.clientWidth / 2;
					});
				}
			}
		};

		renderPages();

		return () => {
			isCancelled = true;
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [pdfDocument, pagesContainerNode, renderZoomScale, baseContentWidth]);

	React.useEffect(() => {
		if (!pagesContainerNode || numPages === 0) return undefined;

		const root = findScrollParent(pagesContainerNode);
		const ratios = new Map<number, number>();

		const observer = new IntersectionObserver(
			(entries) => {
				entries.forEach((entry) => {
					const pageIndex = Number(
						(entry.target as HTMLElement).dataset.pageIndex
					);
					ratios.set(pageIndex, entry.intersectionRatio);
				});

				let bestPageIndex = 0;
				let bestRatio = -1;
				ratios.forEach((ratio, pageIndex) => {
					if (ratio > bestRatio) {
						bestRatio = ratio;
						bestPageIndex = pageIndex;
					}
				});
				onCurrentPageChange?.(bestPageIndex);
			},
			{ root, threshold: [0, 0.25, 0.5, 0.75, 1] }
		);

		pageContainerRefs.current.forEach((node) => observer.observe(node));

		return () => observer.disconnect();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [pagesContainerNode, numPages]);

	React.useEffect(() => {
		if (!selectedFieldId) return;
		const node = overlayRefs.current.get(selectedFieldId);
		node?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
	}, [selectedFieldId]);

	const fieldsByPage = React.useMemo(() => {
		const map = new Map<number, DetectedPdfField[]>();
		detectedFields.filter(isConfigurablePdfField).forEach((field) => {
			const bucket = map.get(field.pageIndex) ?? [];
			bucket.push(field);
			map.set(field.pageIndex, bucket);
		});
		return map;
	}, [detectedFields]);

	if (error) {
		return (
			<div className="flex items-center justify-center h-40 w-full">
				<Typography type="regular-14" className="text-main-red">
					{error}
				</Typography>
			</div>
		);
	}

	if (isLoading || !pdfDocument) {
		return (
			<div className="flex items-center justify-center h-40 w-full">
				<Spinner />
			</div>
		);
	}

	return (
		<div ref={setPagesContainerNode} className="w-full flex flex-col gap-6">
			{Array.from({ length: numPages }).map((_, pageIndex) => {
				const viewport = pageViewports.find((v) => v.pageIndex === pageIndex);
				const pageFields = fieldsByPage.get(pageIndex) ?? [];
				const pageViewport = pageViewportInstances.current.get(pageIndex);

				return (
					<div
						key={pageIndex}
						ref={(node) => {
							if (node) pageContainerRefs.current.set(pageIndex, node);
							else pageContainerRefs.current.delete(pageIndex);
						}}
						className="relative w-full"
						style={{ minHeight: viewport?.height }}
						data-page-index={pageIndex}
					>
						<canvas
							ref={(node) => {
								if (node) canvasRefs.current.set(pageIndex, node);
								else canvasRefs.current.delete(pageIndex);
							}}
							className="block"
						/>

						{pageViewport &&
							pageFields.map((field) => {
								const config = fieldConfigs[field.id];
								const dataType = config?.dataType ?? 'DO_NOT_VALIDATE';
								const renamedLabel = config?.renamedLabel ?? field.fieldName;
								const isSelected = field.id === selectedFieldId;
								const isAssigned = dataType !== 'DO_NOT_VALIDATE';
								const isValidationType =
									dataType !== 'DO_NOT_VALIDATE' && dataType !== 'SIGNATURE';

								const [rx1, ry1, rx2, ry2] =
									pageViewport.convertToViewportRectangle([
										field.rect.x,
										field.rect.y,
										field.rect.x + field.rect.width,
										field.rect.y + field.rect.height,
									]);
								const left = Math.min(rx1, rx2);
								const rawTop = Math.min(ry1, ry2);
								const rawHeight = Math.abs(ry2 - ry1);
								const overlayWidth = Math.abs(rx2 - rx1);
								const overlayHeight = 28;
								const top = rawTop + (rawHeight - overlayHeight) / 2;
								const canShowLabel = overlayWidth >= 28;

								return (
									<button
										key={field.id}
										type="button"
										ref={(node) => {
											if (node) overlayRefs.current.set(field.id, node);
											else overlayRefs.current.delete(field.id);
										}}
										onClick={() => onFieldClick?.(field.id)}
										aria-label={`${renamedLabel}, ${DATA_TYPE_LABELS[dataType]}`}
										aria-pressed={isSelected}
										className="absolute flex items-center justify-start px-1 overflow-hidden focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
										style={{
											left,
											top,
											width: overlayWidth,
											height: overlayHeight,
											borderRadius: 6,
											...(isValidationType
												? {
														border: '2px dashed #2084E773',
														...(isSelected
															? {
																	background: '#EBDFFA',
																	boxShadow: '0 0 0 3px rgba(128,57,223,0.15)',
															  }
															: { background: 'rgba(32,132,231,0.03)' }),
												  }
												: isSelected
												? {
														background: '#EBDFFA',
														border: '2px solid #8039DF',
														boxShadow: '0 0 0 3px rgba(128,57,223,0.15)',
												  }
												: {
														border: '2px dashed rgba(128,57,223,0.45)',
														background: 'rgba(128,57,223,0.03)',
												  }),
										}}
									>
										{canShowLabel && (
											<span className="flex items-center gap-1 w-full overflow-hidden">
												<span
													className="truncate text-left flex-1 min-w-0"
													style={{
														fontSize: 10.5,
														fontWeight: 600,
														color: isValidationType
															? '#2084E773'
															: isSelected
															? '#8039DF'
															: '#8039DFCC',
													}}
												>
													{renamedLabel}
												</span>
												<span
													className="truncate text-left shrink-0"
													style={{
														fontSize: 10,
														fontWeight: 400,
														color: isValidationType
															? '#2084E773'
															: isAssigned
															? 'rgba(128,57,223,0.8)'
															: 'rgba(13,6,22,0.3)',
													}}
												>
													{DATA_TYPE_LABELS[dataType]}
												</span>
											</span>
										)}
									</button>
								);
							})}
					</div>
				);
			})}
		</div>
	);
});

export default PdfFieldCanvas;
